import styled from "@emotion/styled";
import { FC, ReactNode, useCallback } from "react";

import { CheckCircle, Info, OpenInNew, Person } from "@livechat/design-system-icons";
import {
  Alert,
  Button,
  DesignToken,
  FormField,
  Heading,
  Icon,
  Input,
  SpacingToken,
  Tag,
  Text,
  Tooltip,
} from "@livechat/design-system-react-components";

import { CopyToClipboard } from "components/copy-to-clipboard-input";
import { StyledExternalLink } from "components/external-link";
import { IconsEnum, Icon as LegacyIcon } from "components/icon";
import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { IEmailDomainUnverified } from "store/entities/email-domains";
import { withVar } from "themes/helpers";
import { plural } from "utils/string";

import {
  ConfigurationButtonWrapper,
  DNSHeaderSetupContainer,
  DNSSetupContainer,
  SetupDKIMRecordsContainer,
  SetupInstructionsContainer,
  SetupStepContainer,
  StyledBadge,
  failedRecordsListStyles,
  failedRecordsParagraphStyles,
  noteStyles,
  setupHeaderStyles,
} from "./styles";

export const LimitReachedBanner: FC<{ domainsLimit: number }> = ({ domainsLimit }) => (
  <Alert kind="error" css={{ marginBottom: withVar(SpacingToken.Spacing5) }}>
    <Heading size="xs">Domain number limit is reached</Heading>
    <Text>
      You can&apos;t add another domain because you&apos;ve reached the limit of {domainsLimit} custom domains.{" "}
    </Text>
  </Alert>
);

interface ICopyFieldProps {
  value: string;
  labelText: string;
  labelAdornment?: ReactNode;
  onCopy: () => void;
}

export const CopyField: FC<ICopyFieldProps> = ({ value, labelText, labelAdornment, onCopy }) => {
  const handleCopied = useCallback(() => {
    onCopy();
  }, [onCopy]);

  return (
    <CopyToClipboard value={value} onCopiedHandler={handleCopied}>
      {/* TODO: Fix this the next time the file is edited. */}
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      {({ handleCopy }: any) => (
        <CopyFormField labelText={labelText} labelAdornment={labelAdornment}>
          <Flex justifyContent="center" gap={withVar(SpacingToken.Spacing1)}>
            <Input id={`${labelText}-${value}`} value={value || ""} disabled css={{ width: "100%" }} />
            <Button kind="secondary" onClick={handleCopy}>
              Copy
            </Button>
          </Flex>
        </CopyFormField>
      )}
    </CopyToClipboard>
  );
};

export const DomainProviderWarning: FC<{ domainName: string; fieldName: string }> = ({ domainName, fieldName }) => (
  <Tooltip
    kind="invert"
    placement="right"
    triggerRenderer={
      <Block marginBottom={withVar(SpacingToken.Spacing05)}>
        <Icon source={Info} size="small" />
      </Block>
    }
  >
    Some DNS setup require the {fieldName} without the &ldquo;.{domainName}&rdquo; domain.
  </Tooltip>
);

export const DNSSetupDomainHeader: FC<{
  domainName?: string;
  text: string;
  badge?: string;
}> = ({ domainName, text, badge }) => (
  <DNSHeaderSetupContainer>
    <Icon source={CheckCircle} kind="success" />
    <Heading size="xs">{text}</Heading>
    {domainName && <Text customColor={withVar(DesignToken.ContentBasicSecondary)}>{domainName}</Text>}
    {badge && (
      <Tag size="small" kind="success">
        {badge}
      </Tag>
    )}
  </DNSHeaderSetupContainer>
);

export const RecordConfigurationDetails: FC<{
  children: ReactNode;
}> = ({ children }) => (
  <DNSSetupContainer>
    <div css={setupHeaderStyles}>
      <Icon source={Person} />
      <Heading size="xs">Ownership verification</Heading>
    </div>
    {children}
  </DNSSetupContainer>
);

export const RecordConfigurationDomainConnect: FC<{
  targetDomain: IEmailDomainUnverified;
  validationError: { error: string; type: string };
  onPopupClick: () => void;
  loading: boolean;
  handleChangeSetup: () => void;
}> = ({ onPopupClick, targetDomain, validationError, loading, handleChangeSetup }) => (
  <>
    {!validationError.error ? (
      <Alert kind="info">
        <Heading size="xs">Looks like your DNS provider is: {targetDomain?.providerName}</Heading>
        <Text>
          Follow the instructions to add and verify your domain. Any changes you make won&apos;t affect how your domain
          works now.
        </Text>
      </Alert>
    ) : (
      <Alert kind="error">
        <Heading size="xs">
          {validationError.error} {validationError.type === "default" && " Please try again."}
        </Heading>
      </Alert>
    )}
    <SetupInstructionsContainer>
      <Heading size="xs">Verify domain ownership</Heading>
      <ConfigurationButtonWrapper alignItems="baseline">
        <Button
          loading={loading}
          onClick={onPopupClick}
          icon={<Icon source={OpenInNew} kind="primary" />}
          iconPosition="right"
          kind="primary"
        >
          Continue with {targetDomain?.providerName}
        </Button>
        {loading && <Text>This may take several seconds.</Text>}
      </ConfigurationButtonWrapper>
      <Text css={noteStyles}>
        Note: Alternatively, you can add and verify the domain{" "}
        <Button onClick={handleChangeSetup} kind="link-light">
          manually
        </Button>
        .
      </Text>
    </SetupInstructionsContainer>
  </>
);

export const RecordConfigurationManualProvider: FC<{
  hostname: string;
  domainName: string;
  recordType: string;
  value: string;
  onCopy: (recordType: string, field: string) => void;
  targetDomain: IEmailDomainUnverified;
  validationError: { error: string; type: string };
}> = ({ hostname, value, onCopy, targetDomain, recordType, validationError, domainName }) => {
  const handleCopy = useCallback(
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    (field: string) => () => {
      trackEngagement("Settings: Domain record configuration copied", EventPlace.Settings, { recordType, field });
      onCopy(recordType, field);
    },
    [onCopy, recordType]
  );

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const renderInfoBanner = () =>
    targetDomain?.providerName ? (
      <Alert kind="info">
        <Heading size="xs">Looks like your DNS provider is: {targetDomain?.providerName}</Heading>
        {targetDomain.helpcenterURL ? (
          <Text>
            Follow the instructions below to add and verify your domain. For more help, visit your{" "}
            <Button
              onClick={() => trackEngagement("Domains: HelpCenter link clicked", EventPlace.Settings)}
              href={targetDomain?.helpcenterURL}
              target="_blank"
              kind="link-light"
            >
              provider&apos;s help center.
            </Button>
          </Text>
        ) : (
          <Text>
            Follow the instructions below to add and verify your domain. For more help, visit your provider&apos;s help
            center.
          </Text>
        )}
      </Alert>
    ) : (
      <Alert kind="info">
        <Text>Follow the instructions below to set up your domain.</Text>
      </Alert>
    );

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const renderErrorBanner = () => {
    if (validationError.type === "conflict" || validationError.type === "limitExceeded") {
      return (
        <Alert kind="error">
          <Heading size="xs">{validationError.error}</Heading>
        </Alert>
      );
    }

    return (
      <Alert kind="error">
        <Heading size="xs">{validationError.error}</Heading>
        {targetDomain?.helpcenterURL ? (
          <Text>
            Ensure you&apos;ve added all the following records correctly, including endings and punctuation, and then
            try again. For more help, visit your{" "}
            <Button
              onClick={() => trackEngagement("Domains: HelpCenter link clicked", EventPlace.Settings)}
              href={targetDomain?.helpcenterURL}
              target="_blank"
              kind="link-light"
            >
              provider&apos;s help center.
            </Button>
          </Text>
        ) : (
          <Text>
            Ensure you&apos;ve added all the following records correctly, including endings and punctuation, and then
            try again. For more help, visit your provider&apos;s help center.
          </Text>
        )}
      </Alert>
    );
  };

  return (
    <>
      {validationError.error ? renderErrorBanner() : renderInfoBanner()}

      <SetupStepContainer>
        <div>
          <StyledBadge>1</StyledBadge>
          {targetDomain?.controlPanelURL ? (
            <span>
              Log in to your{" "}
              <Button
                href={targetDomain?.controlPanelURL}
                target="_blank"
                kind="link-light"
                icon={<Icon source={OpenInNew} />}
                iconPosition="right"
              >
                domain administration panel
              </Button>
            </span>
          ) : (
            <span>Log in to your domain administration panel</span>
          )}
        </div>
        <div>
          <StyledBadge>2</StyledBadge>
          <span>Go to the DNS setup of your domain and edit DNS records</span>
        </div>
        <div>
          <StyledBadge>3</StyledBadge>
          <span>
            Add a new <strong>CNAME</strong> record with the following value
          </span>
          <CopyField
            value={hostname}
            labelText="Hostname"
            labelAdornment={
              hostname.endsWith(`.${domainName}`) ? (
                <DomainProviderWarning fieldName="Hostname" domainName={domainName} />
              ) : undefined
            }
            onCopy={handleCopy("host name")}
          />
          <CopyField value={value} labelText="Value" onCopy={handleCopy("value")} />
        </div>
        <div>
          <StyledBadge>4</StyledBadge>
          <span>Ensure that all new changes are saved and published to your DNS setup</span>
        </div>
      </SetupStepContainer>
    </>
  );
};

export const RecordConnectionDetailsContainer: FC<{
  children: ReactNode;
}> = ({ children }) => (
  <SetupDKIMRecordsContainer>
    <div css={setupHeaderStyles}>
      <LegacyIcon name={IconsEnum.Settings} width="24px" height="24px" inline />
      <strong>HelpDesk connection</strong>
    </div>
    {children}
  </SetupDKIMRecordsContainer>
);

const mapErrorToFailedRecord = {
  "TXT (DKIM) record missing": "TXT (DKIM)",
  "CNAME (Return-Path) record missing": "CNAME (Return-Path)",
} as const;

export const RecordConnectionDetails: FC<{
  txtDNSRecord: [string, { host: string; value: string }[]];
  cnameDNSRecord: [string, { host: string; value: string }[]];
  validationError: string;
  targetDomain?: IEmailDomainUnverified;
  onCopy: (recordType: string, field: string) => void;
  domainName: string;
  hostname: string;
}> = ({ onCopy, txtDNSRecord, cnameDNSRecord, validationError, targetDomain, domainName, hostname }) => {
  const handleCopy = useCallback(
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    (record: string, field: string) => () => {
      trackEngagement("Settings: Domain record configuration copied", EventPlace.Settings, { record, field });
      onCopy(record, field);
    },
    [onCopy]
  );
  const failed = targetDomain?.errors?.map(
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ({ message }: any) => mapErrorToFailedRecord[message as keyof typeof mapErrorToFailedRecord]
  );

  const labelAdornment = hostname.endsWith(`.${domainName}`) ? (
    <DomainProviderWarning fieldName="Hostname" domainName={domainName} />
  ) : undefined;

  const ExternalLinkOrText = ({
    href,
    eventName,
    children,
  }: {
    href?: string | null;
    eventName: string;
    children: ReactNode;
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  }) =>
    href ? (
      <StyledExternalLink onClick={() => trackEngagement(eventName)} href={href}>
        {children}
      </StyledExternalLink>
    ) : (
      children
    );

  return (
    <>
      {validationError && (
        <Alert kind="error">
          <Heading size="xs">We couldn&apos;t connect HelpDesk to your domain</Heading>
          {failed && failed.length > 0 && (
            <>
              <p css={failedRecordsParagraphStyles}>
                The following record{plural(failed.length, " is missing", "s are missing")} or set up incorrectly:
              </p>
              <ul css={failedRecordsListStyles}>
                {/* TODO: Fix this the next time the file is edited. */}
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                {failed.map((record: any) => (
                  <li key={record}>{record}</li>
                ))}
              </ul>
            </>
          )}
          <br />
          <Text>
            Ensure you&apos;ve added these records correctly in your{" "}
            <ExternalLinkOrText href={targetDomain?.controlPanelURL} eventName="Domains: DNS admin panel link clicked">
              domain control panel
            </ExternalLinkOrText>
            , including endings and punctuation, and then try again.
            <br />
            <br />
            For more help, visit your{" "}
            <ExternalLinkOrText href={targetDomain?.helpcenterURL} eventName="Domains: HelpCenter link clicked">
              provider&apos;s help center
            </ExternalLinkOrText>
            .
          </Text>
        </Alert>
      )}
      <SetupStepContainer>
        <div>
          <StyledBadge>1</StyledBadge>
          <span>Continue in the DNS setup</span>
        </div>
        <div>
          <StyledBadge>2</StyledBadge>
          <span>
            Add a new <strong>TXT</strong> record with the following value
          </span>
          <CopyField
            value={txtDNSRecord[1][0].host}
            labelText="Hostname"
            labelAdornment={labelAdornment}
            onCopy={handleCopy("TXT", "host name")}
          />
          <CopyField value={txtDNSRecord[1][0].value} labelText="Value" onCopy={handleCopy("TXT", "value")} />
        </div>
        <div>
          <StyledBadge>3</StyledBadge>
          <span>
            Add a new <strong>CNAME</strong> record with the following value
          </span>
          <CopyField
            value={cnameDNSRecord[1][0].host}
            labelText="Hostname"
            labelAdornment={labelAdornment}
            onCopy={handleCopy("CNAME", "host name")}
          />
          <CopyField value={cnameDNSRecord[1][0].value} labelText="Value" onCopy={handleCopy("CNAME", "value")} />
        </div>
        <div>
          <StyledBadge>4</StyledBadge>
          <span>Ensure that all new changes are saved and published to your DNS setup</span>
        </div>
      </SetupStepContainer>
    </>
  );
};

export const RecordConnectionDetailsDomainConnect: FC<{
  targetDomain: IEmailDomainUnverified;
  validationError: string;
  onPopupClick: () => void;
  loading: boolean;
  handleChangeSetup: () => void;
}> = ({ onPopupClick, targetDomain, validationError, loading, handleChangeSetup }) => (
  <>
    {validationError && (
      <Alert kind="error">
        <Heading size="xs">We couldn&apos;t connect HelpDesk to your domain. Please try again.</Heading>
      </Alert>
    )}
    <SetupInstructionsContainer>
      <Heading size="xs">Connect your domain with HelpDesk</Heading>
      <ConfigurationButtonWrapper alignItems="baseline">
        <Button
          loading={loading}
          onClick={onPopupClick}
          icon={<Icon source={OpenInNew} kind="primary" />}
          iconPosition="right"
          kind="primary"
        >
          Continue with {targetDomain?.providerName}
        </Button>
        {loading && <Text>This may take several seconds.</Text>}
      </ConfigurationButtonWrapper>
      <Text css={noteStyles}>
        Note: Alternatively, you can add and verify the domain{" "}
        <Button onClick={handleChangeSetup} kind="link-light">
          manually
        </Button>
        .
      </Text>
    </SetupInstructionsContainer>
  </>
);

const CopyFormField = styled(FormField)`
  width: 100%;
  padding-left: ${withVar(SpacingToken.Spacing9)};
  padding-top: ${withVar(SpacingToken.Spacing2)};
  margin: 0;
`;
